import { toDate } from '../../utils/dates';

export const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export const startOfDay = (value) => {
    const date = toDate(value);
    if (!date) return null;
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
};

export const dayValue = (value) => {
    const date = startOfDay(value);
    return date ? date.getTime() : null;
};

export const isSameDay = (a, b) => {
    const left = dayValue(a);
    const right = dayValue(b);
    return left !== null && left === right;
};

export const addMonths = (value, amount) => {
    const date = toDate(value) || new Date();
    // Anchor on the 1st so stepping from the 31st doesn't skip a short month.
    return new Date(date.getFullYear(), date.getMonth() + amount, 1);
};

export const addDays = (value, amount) => {
    const date = toDate(value) || new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
};

export const startOfMonth = (value) => {
    const date = toDate(value) || new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

// Always six weeks, so the panel never changes height as you page through months.
export const buildMonthGrid = (year, month) => {
    const first = new Date(year, month, 1);
    const leading = (first.getDay() + 6) % 7; // shift Sunday-first to Monday-first
    const start = new Date(year, month, 1 - leading);

    return Array.from({ length: 6 }, (_, week) =>
        Array.from({ length: 7 }, (_, day) =>
            new Date(start.getFullYear(), start.getMonth(), start.getDate() + week * 7 + day)
        )
    );
};

export const isWithinInterval = (value, { start, end }) => {
    const day = dayValue(value);
    const from = dayValue(start);
    const to = dayValue(end);
    if (day === null || from === null || to === null) return false;
    return day >= Math.min(from, to) && day <= Math.max(from, to);
};

export const isDayDisabled = (value, { minDate, maxDate, excludeIntervals = [] } = {}) => {
    const day = dayValue(value);
    if (day === null) return true;

    const min = dayValue(minDate);
    const max = dayValue(maxDate);
    if (min !== null && day < min) return true;
    if (max !== null && day > max) return true;

    return excludeIntervals.some(interval => isWithinInterval(value, interval));
};

// Once a range start is picked, the end can't jump over an excluded interval —
// so the nearest exclusion after the start becomes a hard ceiling.
export const ceilingAfter = (start, excludeIntervals = []) => {
    const from = dayValue(start);
    if (from === null) return null;

    const nextBlocked = excludeIntervals
        .map(({ start: blockStart, end: blockEnd }) => {
            const values = [dayValue(blockStart), dayValue(blockEnd)].filter(value => value !== null);
            return values.length ? Math.min(...values) : null;
        })
        .filter(value => value !== null && value > from)
        .sort((a, b) => a - b)[0];

    return nextBlocked === undefined ? null : addDays(new Date(nextBlocked), -1);
};

export const clampToRange = (value, minDate, maxDate) => {
    const day = dayValue(value);
    const min = dayValue(minDate);
    const max = dayValue(maxDate);
    if (day === null) return toDate(value);
    if (min !== null && day < min) return startOfDay(minDate);
    if (max !== null && day > max) return startOfDay(maxDate);
    return toDate(value);
};

// Copies hours/minutes from `time` onto the calendar day of `date`.
export const withTimeOf = (date, time) => {
    const day = toDate(date) || new Date();
    const source = toDate(time);
    const merged = new Date(day);
    merged.setHours(source ? source.getHours() : 0, source ? source.getMinutes() : 0, 0, 0);
    return merged;
};

export const withMinutes = (date, minutesSinceMidnight) => {
    const base = toDate(date) || new Date();
    const merged = new Date(base);
    merged.setHours(Math.floor(minutesSinceMidnight / 60), minutesSinceMidnight % 60, 0, 0);
    return merged;
};
